import { useState, useEffect } from "react";
import { Loader2, Sparkles, Plus, Eye, EyeOff, BookText, ArrowRight, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoadingRow, EmptyState } from "./shared/ui";

// -------- Vocabulary Admin --------
export function VocabularyAdmin() {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [newTerm, setNewTerm] = useState("");
  const [creating, setCreating] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [candBusyId, setCandBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, cands] = await Promise.all([
        api.get("/admin/vocabulary"),
        api.get("/admin/vocab-candidates").then((r) => r.data).catch(() => []),
      ]);
      setTerms(data);
      setCandidates(cands || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const activateCandidate = async (c) => {
    setCandBusyId(c.id);
    try {
      const { data } = await api.post(`/admin/vocab-candidates/${c.id}/activate`);
      setCandidates((prev) => prev.filter((x) => x.id !== c.id));
      setTerms((prev) => (prev.some((t) => t.id === data.id) ? prev : [...prev, data].sort((a, b) => a.term.localeCompare(b.term))));
      toast.success(`"${c.term}" toegevoegd aan de woordenlijst.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCandBusyId(null);
    }
  };

  const rejectCandidate = async (c) => {
    setCandBusyId(c.id);
    try {
      await api.post(`/admin/vocab-candidates/${c.id}/reject`);
      setCandidates((prev) => prev.filter((x) => x.id !== c.id));
      toast.success(`"${c.term}" afgewezen.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCandBusyId(null);
    }
  };

  const addTerm = async () => {
    const t = newTerm.trim();
    if (!t) {
      toast.error("Term mag niet leeg zijn.");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/vocabulary", { term: t, active: true });
      setTerms((prev) => [...prev, data].sort((a, b) => a.term.localeCompare(b.term)));
      setNewTerm("");
      toast.success("Term toegevoegd.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (v) => {
    setSavingId(v.id);
    try {
      const { data } = await api.patch(`/admin/vocabulary/${v.id}`, { active: !v.active });
      setTerms((prev) => prev.map((t) => (t.id === v.id ? data : t)));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const activeCount = terms.filter((t) => t.active).length;

  return (
    <div data-testid="admin-vocabulary-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookText size={14} className="text-tw-yellow" strokeWidth={2.5} />
          <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">
            WOORDENLIJST
          </div>
        </div>
        <div className="font-mono text-sm text-tw-yellow" data-testid="admin-vocabulary-count">
          {activeCount}
          <span className="text-tw-muted"> / {terms.length}</span>
        </div>
      </div>

      <p className="text-xs text-tw-muted mb-4 max-w-xl">
        TW1 jargon en namen. Actieve termen worden meegegeven aan Whisper en na-gecorrigeerd op alle transcripties (Web Speech en server).
      </p>

      <div className="flex gap-2 mb-4">
        <Input
          value={newTerm}
          onChange={(e) => setNewTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") addTerm();
          }}
          placeholder="Nieuwe term (bv. DailyOps of Truckstop 8)"
          data-testid="admin-vocab-new-input"
          className="bg-tw-black border-tw text-white h-10 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow"
        />
        <Button
          onClick={addTerm}
          disabled={creating || !newTerm.trim()}
          data-testid="admin-vocab-new-save"
          className="h-10 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold disabled:opacity-50 px-4"
        >
          {creating ? <Loader2 className="animate-spin" size={14} /> : (
            <>
              <Plus size={12} className="mr-1" strokeWidth={3} />
              Add
            </>
          )}
        </Button>
      </div>

      {/* R5 — Voorgesteld: AI-jargonkandidaten onder de drempel */}
      {candidates.length > 0 && (
        <div className="mb-6 border border-tw-yellow/30 bg-tw-yellow/5 p-3" data-testid="admin-vocab-candidates">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={13} className="text-tw-yellow" strokeWidth={2.5} />
            <div className="font-mono text-[10px] tracking-[0.3em] text-tw-yellow">VOORGESTELD</div>
          </div>
          <p className="text-[11px] text-tw-muted mb-3 max-w-xl">
            Termen die de AI vaak hoort maar nog niet in de lijst staan. Vanaf 3 memo's van 2 sprekers worden ze automatisch actief.
          </p>
          <div className="flex flex-col gap-2">
            {candidates.map((c) => (
              <div
                key={c.id}
                data-testid={`admin-vocab-candidate-${c.id}`}
                className="flex items-center justify-between gap-3 bg-tw-black border border-tw px-3 py-2"
              >
                <div className="min-w-0">
                  <span className="font-mono text-sm text-white">{c.term}</span>
                  <span className="ml-2 font-mono text-[10px] text-tw-muted">
                    {c.memo_count} memo{c.memo_count === 1 ? "" : "'s"} · {c.speaker_count} spreker{c.speaker_count === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    onClick={() => activateCandidate(c)}
                    disabled={candBusyId === c.id}
                    data-testid={`admin-vocab-candidate-activate-${c.id}`}
                    className="h-7 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.15em] text-[9px] font-bold px-2.5 disabled:opacity-40"
                  >
                    {candBusyId === c.id ? <Loader2 size={11} className="animate-spin" /> : "Activeren"}
                  </Button>
                  <button
                    type="button"
                    onClick={() => rejectCandidate(c)}
                    disabled={candBusyId === c.id}
                    data-testid={`admin-vocab-candidate-reject-${c.id}`}
                    className="h-7 px-2 border border-tw text-tw-muted hover:text-tw-red hover:border-tw-red transition-colors font-mono text-[9px] uppercase tracking-[0.15em] disabled:opacity-40"
                  >
                    Afwijzen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <LoadingRow />
      ) : terms.length === 0 ? (
        <EmptyState label="Nog geen termen" hint="Voeg TW1-jargon toe voor betere transcripten." testId="admin-vocab-empty" />
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="admin-vocab-list">
          {terms.map((t) => (
            <span
              key={t.id}
              data-testid={`admin-vocab-${t.id}`}
              className={`inline-flex items-center gap-2 px-2.5 py-1 border font-mono text-xs ${
                t.active
                  ? "bg-tw-surface border-tw text-white"
                  : "bg-transparent border-tw text-tw-muted line-through"
              }`}
            >
              <span>{t.term}</span>
              {t.source === "ai" && (
                <span
                  data-testid={`admin-vocab-ai-badge-${t.id}`}
                  className="inline-flex items-center gap-0.5 px-1 text-[9px] font-bold tracking-[0.1em] text-black bg-tw-yellow"
                  title="Automatisch geleerd door AI"
                >
                  <Sparkles size={8} strokeWidth={3} /> AI
                </span>
              )}
              <button
                type="button"
                onClick={() => toggleActive(t)}
                disabled={savingId === t.id}
                data-testid={`admin-vocab-toggle-${t.id}`}
                title={t.active ? "Deactiveer" : "Activeer"}
                aria-label={t.active ? "Deactiveer" : "Activeer"}
                className="text-tw-muted hover:text-tw-yellow transition-colors disabled:opacity-40"
              >
                {savingId === t.id ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : t.active ? (
                  <Eye size={12} strokeWidth={2.5} />
                ) : (
                  <EyeOff size={12} strokeWidth={2.5} />
                )}
              </button>
            </span>
          ))}
        </div>
      )}

      <CorrectionPairs />
    </div>
  );
}

// -------- Round H: correctieparen (verkeerd -> goed) --------
export function CorrectionPairs() {
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wrong, setWrong] = useState("");
  const [right, setRight] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/corrections");
      setPairs(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addPair = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/corrections", { wrong: wrong.trim(), right: right.trim(), active: true });
      setPairs((prev) => [...prev, data].sort((a, b) => a.wrong.localeCompare(b.wrong)));
      setWrong("");
      setRight("");
      setPreview(null);
      toast.success("Correctiepaar toegevoegd.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (pair) => {
    setSavingId(pair.id);
    try {
      const { data } = await api.patch(`/admin/corrections/${pair.id}`, { active: !pair.active });
      setPairs((prev) => prev.map((p) => (p.id === pair.id ? data : p)));
      setPreview(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const runPreview = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/admin/corrections/apply", { confirm: false });
      setPreview(data);
      if (!data.matched) toast.success("Geen bestaande memo's veranderen hierdoor.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  const runApply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/admin/corrections/apply", { confirm: true });
      setPreview(null);
      toast.success(`${data.applied} memo${data.applied === 1 ? "" : "'s"} bijgewerkt. Het originele transcript blijft bewaard.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  const activeCount = pairs.filter((p) => p.active).length;

  return (
    <div className="mt-8 pt-6 border-t border-tw" data-testid="admin-corrections-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wand2 size={14} className="text-tw-yellow" strokeWidth={2.5} />
          <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">CORRECTIEPAREN</div>
        </div>
        <div className="font-mono text-sm text-tw-yellow" data-testid="admin-corrections-count">
          {activeCount}
          <span className="text-tw-muted"> / {pairs.length}</span>
        </div>
      </div>

      <p className="text-xs text-tw-muted mb-4 max-w-xl">
        Verkeerd → goed. Vocal past actieve paren toe op de weergegeven tekst van nieuwe memo's en op de
        AI-titel en samenvatting. Hele woorden, ongeacht hoofdletters. Het originele transcript blijft altijd onaangeroerd.
      </p>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Input
          value={wrong}
          onChange={(e) => setWrong(e.target.value)}
          placeholder="Verkeerd (bv. truckwas)"
          data-testid="admin-correction-wrong-input"
          className="bg-tw-black border-tw text-white h-10 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow"
        />
        <Input
          value={right}
          onChange={(e) => setRight(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && wrong.trim() && right.trim()) addPair();
          }}
          placeholder="Goed (bv. Truckwash1)"
          data-testid="admin-correction-right-input"
          className="bg-tw-black border-tw text-white h-10 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow"
        />
        <Button
          onClick={addPair}
          disabled={busy || !wrong.trim() || !right.trim()}
          data-testid="admin-correction-add"
          className="h-10 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold disabled:opacity-50 px-4 shrink-0"
        >
          {busy ? <Loader2 className="animate-spin" size={14} /> : (
            <>
              <Plus size={12} className="mr-1" strokeWidth={3} />
              Toevoegen
            </>
          )}
        </Button>
      </div>

      {loading ? (
        <LoadingRow />
      ) : pairs.length === 0 ? (
        <EmptyState label="Nog geen correctieparen" hint="Voeg een paar toe als Whisper een woord steeds fout schrijft." testId="admin-corrections-empty" />
      ) : (
        <div className="flex flex-col gap-2" data-testid="admin-corrections-list">
          {pairs.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-correction-${p.id}`}
              className={`flex items-center justify-between gap-3 border border-tw px-3 py-2 ${p.active ? "bg-tw-surface" : "bg-transparent text-tw-muted"}`}
            >
              <div className="min-w-0 font-mono text-xs flex items-center gap-2 flex-wrap">
                <span className={p.active ? "text-tw-muted" : "line-through"}>{p.wrong}</span>
                <ArrowRight size={11} className="text-tw-yellow shrink-0" strokeWidth={3} />
                <span className={p.active ? "text-white" : "line-through"}>{p.right}</span>
              </div>
              <button
                type="button"
                onClick={() => toggle(p)}
                disabled={savingId === p.id}
                data-testid={`admin-correction-toggle-${p.id}`}
                title={p.active ? "Deactiveer" : "Activeer"}
                aria-label={p.active ? "Deactiveer" : "Activeer"}
                className="text-tw-muted hover:text-tw-yellow transition-colors disabled:opacity-40 shrink-0"
              >
                {savingId === p.id ? <Loader2 size={12} className="animate-spin" /> : p.active ? <Eye size={12} strokeWidth={2.5} /> : <EyeOff size={12} strokeWidth={2.5} />}
              </button>
            </div>
          ))}
        </div>
      )}

      {activeCount > 0 && (
        <div className="mt-4">
          <Button
            onClick={runPreview}
            disabled={applying}
            data-testid="admin-corrections-preview-btn"
            className="h-9 rounded-none bg-transparent border border-tw-yellow text-tw-yellow hover:bg-tw-yellow hover:text-black uppercase tracking-[0.2em] text-[10px] font-bold px-4 disabled:opacity-50"
          >
            {applying && !preview ? <Loader2 className="animate-spin" size={14} /> : "Toepassen op bestaande memo's"}
          </Button>
        </div>
      )}

      {preview && (
        <div className="mt-4 border border-tw-yellow/40 bg-tw-yellow/5 p-3" data-testid="admin-corrections-preview">
          <p className="text-sm text-white mb-2" data-testid="admin-corrections-preview-count">
            {preview.matched === 0
              ? "Geen bestaande memo's veranderen hierdoor."
              : `${preview.matched} memo${preview.matched === 1 ? "" : "'s"} verandert hierdoor. Nog niets opgeslagen.`}
          </p>
          {preview.examples?.map((ex, i) => (
            <div key={ex.id} className="border-t border-tw py-2 font-mono text-[11px]" data-testid={`admin-corrections-example-${i}`}>
              <p className="text-tw-muted break-words">Voor: {ex.before}</p>
              <p className="text-white break-words mt-1">
                Na: {ex.highlight
                  ? ex.after.split(ex.highlight).map((part, idx, arr) => (
                      <span key={idx}>
                        {part}
                        {idx < arr.length - 1 && (
                          <mark className="bg-tw-yellow text-black px-0.5" data-testid={`admin-corrections-example-${i}-mark`}>{ex.highlight}</mark>
                        )}
                      </span>
                    ))
                  : ex.after}
              </p>
            </div>
          ))}
          {preview.matched > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                onClick={runApply}
                disabled={applying}
                data-testid="admin-corrections-apply-btn"
                className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold px-4 disabled:opacity-50"
              >
                {applying ? <Loader2 className="animate-spin" size={14} /> : "Definitief toepassen"}
              </Button>
              <button
                type="button"
                onClick={() => setPreview(null)}
                data-testid="admin-corrections-cancel"
                className="h-9 px-3 border border-tw text-tw-muted hover:text-white transition-colors font-mono text-[10px] uppercase tracking-[0.2em]"
              >
                Annuleren
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
